import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import request from "../../../api/request";
import { BackendAPI } from "../../../const/api";
import { AuthContext } from "../../../auth/authContext";
import { useToast } from "../../../component/ToastBar";
import { MaterialAttributeField } from "../../../const/model";
import EditModal, { EditField } from "../../../component/EditModal/EditModal";
import {
  Autocomplete,
  Box,
  Button,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterPanel, {
  FilterOption,
  getSimpleMaterials,
  QueryOptionsItem,
} from "../../../component/FilterPanel/FilterPanel";
import "./ListPage.css";
import { getLastSyncTime } from "../../../services";
import {FieldType} from "../../../const/enum";

interface MaterialAttributes {
  id: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  materialModel: string;
  attributes: { [attributeName: string]: string };
  kdPriceLimit: {
    code: string;
    value: number;
  }[];
  version: number;
  validFromDate: Date;
  validToDate: Date;
  createdByName: string;
}

interface PriceList {
  code: string;
  name: string;
}

interface MaterialAttributeHistory {
  version: number;
  validFromDate: Date;
  diff: {
    attributes: {
      [attributeName: number]: {
        old: string;
        new: string;
      };
    };
    kdPriceLimit: {
      code: string;
      old: string;
      new: string;
    }[];
  };
  createdByName: string;
}

const MaterialAttributesPage = () => {
  const authContext = useContext(AuthContext);
  const [fields, setFields] = useState<MaterialAttributeField[]>([]);
  const [attributes, setAttributes] = useState<MaterialAttributes[]>([]);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [editData, setEditData] = useState<MaterialAttributes | null>(null);
  const [currentPriceList, setCurrentPriceList] = useState<PriceList | null>(
    null,
  );
  const [priceListOptions, setPriceListOptions] = useState<QueryOptionsItem[]>(
    [],
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [materialAttrHistories, setMaterialAttrHistories] = useState<
    MaterialAttributeHistory[]
  >([]);
  const [materialOptions, setMaterialOptions] = useState<QueryOptionsItem[]>(
    [],
  );
  const [filter, setFilter] = useState<any>({
    materials: [],
    material: "",
    filterNoPrice: 1,  // 默认显示仅有价格的物料
  });
  const [syncRes, setSyncRes] = useState<{
    created: number;
    updated: number;
    materialUpdated: number;
  } | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState("");

  const toast = useToast();

  const fetchAttributeFields = useCallback(async () => {
    try {
      const response = await request.get(BackendAPI.getAttributeFields);
      if (!response.success) {
        console.error("Error fetching material attributes:", response.message);
      }
      return response.data.attributes;
    } catch (error) {
      console.error("Error fetching material attributes:", error);
      return [];
    }
  }, []);

  const fetchMaterialAttributes = useCallback(async () => {
    try {
      const showFilterNoPrice =
        authContext?.isFinance() ||
        authContext?.isAdmin() ||
        authContext?.isPurchaser();
      const filterNoPrice = showFilterNoPrice
        ? filter.filterNoPrice === undefined
          ? 1
          : filter.filterNoPrice
        : 1;
      const response = await request.get(BackendAPI.getMaterialAttributes, {
        materials: filter.materials.length > 0 ? filter.materials : [],
        material: filter.material ? filter.material : "",
        filterNoPrice: filterNoPrice,
        startDate: filter.startDate,
        endDate: filter.endDate,
        page: page + 1,
        size: rowsPerPage,
      });
      if (!response.success) {
        const message = response.message;
        console.error("Error fetching material attributes:", response.message);
        toast(message, "error");
      }
      return response.data.data;
    } catch (error) {
      console.error("Error fetching material attributes:", error);
      return {
        materials: [],
        total: 0,
      };
    }
  }, [filter, page, rowsPerPage, toast, authContext]);

  const updateMaterialAttributes = useCallback(async () => {
    if (!editData) {
      return;
    }
    const response = await request.put(BackendAPI.updateMaterialAttributes, {
      id: editData.id,
      materialId: editData.materialId,
      attributes: editData.attributes,
      kdPriceLimit: editData.kdPriceLimit,
    });
    if (!response.success) {
      const message = response.message;
      console.error("Error updating material attributes:", response.message);
      toast(message, "error");
    } else {
      toast("保存成功");
    }
  }, [editData, toast]);

  const fetchMaterialAttributesHistory = useCallback(
    async (materialId: string) => {
      try {
        const response = await request.get(
          BackendAPI.getMaterialAttributesHistory,
          {
            materialId: materialId,
          },
        );
        if (!response.success) {
          console.error(
            "Error fetching material attributes history:",
            response.message,
          );
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching material attributes history:", error);
        return [];
      }
    },
    [],
  );

  const fetchMaterials = useCallback(async () => {
    return await getSimpleMaterials();
  }, []);

  const fetchPriceLists = useCallback(async () => {
    try {
      const response = await request.get(BackendAPI.allPriceList);
      if (!response.success) {
        console.error("Error fetching price lists:", response.message);
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching price lists:", error);
      return [];
    }
  }, []);

  const fetchLastSyncTime = async () => {
    const lastSyncTime = await getLastSyncTime("customer_price_list");
    setLastSyncTime(lastSyncTime);
  };

  const handleSync = async () => {
    request.get(BackendAPI.syncPriceList).then((res) => {
      if (res.success) {
        setSyncRes({
          created: res.data.created,
          updated: res.data.updated,
          materialUpdated: res.data.materialUpdated,
        });
        toast("同步成功");
        fetchLastSyncTime().then();
        fetchMaterialAttributes().then((res) => {
          setAttributes(res.materials);
          setTotalCount(res.total);
        });
      }
    });
  };

  const handleExport = async () => {
    request.get(BackendAPI.exportMaterialAttributes).then((res) => {
      console.log(res)
      const url = window.URL.createObjectURL(res);
      window.open(url, "_blank");
    });
  };

  useEffect(() => {
    fetchAttributeFields().then((res) => setFields(res));
  }, [fetchAttributeFields]);

  useEffect(() => {
    fetchMaterialAttributes().then((res) => {
      setAttributes(res.materials);
      setTotalCount(res.total);
    });
  }, [fetchMaterialAttributes]);

  useEffect(() => {
    fetchPriceLists().then((res) => {
      const defaultList = res.default[0];
      setCurrentPriceList(defaultList);
      const commonList = res.common || [];
      const allPriceList = [res.default[0], ...commonList];
      const options = allPriceList.map(
        (priceList: PriceList) =>
          ({
            value: priceList.code,
            label: priceList.name,
          }) as QueryOptionsItem,
      );
      setPriceListOptions(options);
    });
  }, [fetchPriceLists]);

  useEffect(() => {
    fetchMaterials().then((res) => setMaterialOptions(res));
  }, [fetchMaterials]);

  useEffect(() => {
    fetchLastSyncTime().then();
  }, []);

  const attributeFieldsToColumnTitles = (fields: MaterialAttributeField[]) => {
    return fields.map((field) => (
      <Tooltip title={field.description}>
        <TableCell className="column-width-small">
          <div>{field.name}</div>
        </TableCell>
      </Tooltip>
    ));
  };

  const attributeFieldsToColumnCells = (
    material: MaterialAttributes,
    fields: MaterialAttributeField[],
  ) => {
    return fields.map((field) => {
      const value = material.attributes[field.id];
      const text = value
        ? field.fieldType === FieldType.PERCENTAGE
          ? `${value} %`
          : value
        : "";
      if (field.fieldType === FieldType.URL && text) {
        return (
          <Tooltip title={text}>
            <TableCell className="column-width-medium">
              <Button
                onClick={() => {
                  window.open(text);
                }}
              >
                跳转
              </Button>
              <Button
                onClick={() => {
                  navigator.clipboard
                    .writeText(text)
                    .then(() => toast("复制成功"));
                }}
              >
                复制
              </Button>
            </TableCell>
          </Tooltip>
        );
      }
      return (
        <Tooltip title={text}>
          <TableCell className="column-width-small">{text}</TableCell>
        </Tooltip>
      );
    });
  };

  const filterOptions = useMemo(() => {
    const options: FilterOption[] = [
      {
        key: "materials",
        label: "物料",
        type: "multiselect",
        options: materialOptions,
      },
      {
        key: "material",
        label: "物料模糊搜索",
        type: "text",
      },
    ];
    if (
      authContext?.isFinance() ||
      authContext?.isAdmin() ||
      authContext?.isPurchaser()
    ) {
      options.push({
        key: "filterNoPrice",
        label: "有无价格",
        type: "select",
        options: [
          { value: "0", label: "全部" },
          { value: "1", label: "仅有价格" },
          { value: "2", label: "仅无价格" },
        ] as QueryOptionsItem[],
        defaultValue: [{ value: "1", label: "仅有价格" }],
      });
    }
    return options;
  }, [materialOptions, authContext]);

  const onFilterChange = (filters: any) => {
    // 处理filterNoPrice，可能是数组、对象或undefined
    let filterNoPrice = 1; // 默认值
    if (filters.filterNoPrice) {
      if (Array.isArray(filters.filterNoPrice)) {
        // 如果是数组，取第一个元素
        const firstItem = filters.filterNoPrice[0];
        filterNoPrice = firstItem?.value ? parseInt(firstItem.value) : 1;
      } else if (filters.filterNoPrice.value) {
        // 如果是对象
        filterNoPrice = parseInt(filters.filterNoPrice.value);
      } else {
        filterNoPrice = parseInt(filters.filterNoPrice);
      }
    }

    const newFilter = {
      materials: filters.materials?.map((m) => m.value) || [],
      material: filters.material,
      filterNoPrice: filterNoPrice,
      startDate: filters.start_date,
      endDate: filters.end_date,
    };
    setFilter(newFilter);
  };

  const editColumns = (): EditField[] => {
    const columns: EditField[] = [
      {
        label: "物料编码",
        type: "text",
        value: editData?.materialCode,
        onChange: undefined,
      },
      {
        label: "物料名",
        type: "text",
        value: editData?.materialName,
        onChange: undefined,
      },
      {
        label: "型号规格",
        type: "text",
        value: editData?.materialModel,
        onChange: undefined,
      },
    ];
    columns.push(
      ...(fields
        .map((field: MaterialAttributeField) => {
          const { id, name, fieldType, editRoles } = field;
          const currentRole = authContext?.user?.role;
          if (!currentRole || !editRoles.includes(currentRole)) {
            return undefined;
          }
          let type = "text";
          if (
            fieldType === FieldType.NUMBER ||
            fieldType === FieldType.PERCENTAGE
          ) {
            type = "number";
          }
          return {
            label: name,
            type: type,
            value: editData?.attributes[id],
            onChange: (value: any) => {
              if (editData) {
                const temp = {
                  ...editData,
                };
                temp.attributes[id] = value;
                setEditData(temp);
              }
            },
          } as EditField;
        })
        .filter((c) => {
          return c !== undefined;
        }) as EditField[]),
    );
    return columns;
  };

  const handleEditClick = (material: MaterialAttributes) => {
    setEditData({
      ...material,
    });
    setShowEdit(true);
    setPopoverAnchorEl(null);
    setShowPopover(false);
  };

  const handleDetailClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>, materialId: string) => {
      setPopoverAnchorEl(event.currentTarget);
      setShowPopover(true);
      const histories = await fetchMaterialAttributesHistory(materialId);
      setMaterialAttrHistories(histories);
    },
    [fetchMaterialAttributesHistory],
  );

  const onSave = () => {
    updateMaterialAttributes().then(() => {
      setShowEdit(false);
      fetchMaterialAttributes().then((res) => {
        setAttributes(res.materials);
        setTotalCount(res.total);
      });
    });
  };

  return (
    <div>
      <FilterPanel
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
      />
      {(authContext?.isBA() ||
        authContext?.isAdmin() ||
        authContext?.isFinance()) && (
        <div>
          <Button onClick={handleSync}>同步金蝶限价</Button>
          {syncRes && (
            <span>
              本次：创建{`${syncRes.created}`}条, 更新{`${syncRes.updated}`}条
            </span>
          )}
          <div />
          {lastSyncTime ? (
            <span>上次同步时间: {lastSyncTime}</span>
          ) : (
            <span>上次同步时间: 从未同步</span>
          )}
        </div>
      )}
      {authContext?.isAdmin() && (
        <div>
          <Button onClick={handleExport}>导出价格表</Button>
        </div>
      )}
      <Autocomplete
        sx={{ width: 300 }}
        options={priceListOptions}
        getOptionLabel={(option) => option.label}
        value={
          currentPriceList
            ? {
                value: currentPriceList.code,
                label: currentPriceList.name,
              }
            : null
        }
        onChange={(event, newValue) => {
          if (newValue) {
            setCurrentPriceList({
              code: newValue.value,
              name: newValue.label,
            } as PriceList);
          }
        }}
        renderInput={(params) => <TextField {...params} label="价格表" />}
      />
      <Table className="list-table">
        <TableHead>
          <TableRow>
            <TableCell className="sortable-column">操作</TableCell>
            <TableCell className="sortable-column">物料编码</TableCell>
            <TableCell className="sortable-column">物料名</TableCell>
            <TableCell className="sortable-column">型号规格</TableCell>
            {currentPriceList && (
              <Tooltip title={currentPriceList.name}>
                <TableCell className="column-width-medium">
                  {currentPriceList.name}
                </TableCell>
              </Tooltip>
            )}
            {attributeFieldsToColumnTitles(fields)}
          </TableRow>
        </TableHead>
        <TableBody>
          {attributes.map((material: MaterialAttributes) => {
            const kdValue = material.kdPriceLimit.find(
              (item) => item.code === currentPriceList?.code,
            );
            return (
              <TableRow key={material.id}>
                <TableCell>
                  <Button
                    disabled={
                      authContext &&
                      !authContext.isFinance() &&
                      !authContext.isPurchaser() &&
                      !authContext.isAdmin()
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(material);
                    }}
                  >
                    编辑
                  </Button>
                  <Button
                    onClick={(e) => {
                      handleDetailClick(e, material.materialId);
                    }}
                    disabled={material.version <= 1}
                  >
                    历史
                  </Button>
                </TableCell>
                <Tooltip title={material.materialCode}>
                  <TableCell className="column-width-medium">
                    {material.materialCode}
                  </TableCell>
                </Tooltip>
                <Tooltip title={material.materialName}>
                  <TableCell className="column-width-medium">
                    {material.materialName}
                  </TableCell>
                </Tooltip>
                <Tooltip title={material.materialModel}>
                  <TableCell className="column-width-medium">
                    {material.materialModel}
                  </TableCell>
                </Tooltip>
                {currentPriceList && (
                  <TableCell>{kdValue ? kdValue.value : "无"}</TableCell>
                )}
                {attributeFieldsToColumnCells(material, fields)}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />
      <Popover
        id={"detail-popover"}
        open={showPopover}
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setShowPopover(false);
          setPopoverAnchorEl(null);
          setMaterialAttrHistories([]);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus
      >
        <Typography sx={{ p: 2 }}>
          {materialAttrHistories ? (
            <Typography>
              {materialAttrHistories.map(
                (history: MaterialAttributeHistory) => (
                  <MaterialAttributeHistoryTypography
                    history={history}
                    fields={fields}
                  />
                ),
              )}
            </Typography>
          ) : (
            ""
          )}
        </Typography>
      </Popover>
      {showEdit && (
        <EditModal
          fields={editColumns()}
          onClose={() => setShowEdit(false)}
          onSave={onSave}
        />
      )}
    </div>
  );
};

interface MaterialAttributeHistoryPanelProps {
  history: MaterialAttributeHistory;
  fields: MaterialAttributeField[];
}

const MaterialAttributeHistoryTypography = ({
  history,
  fields,
}: MaterialAttributeHistoryPanelProps) => {
  console.log(history.diff.kdPriceLimit);
  const oldNewText = (value) => {
    const oldText = value.old ? value.old : "无";
    const newText = value.new ? value.new : "无";
    // return `原：${oldText} => 新：${newText}`;
    return (
      <span>
        <span>原：</span>
        <span style={{ fontWeight: "bold" }}>{oldText}</span>
        <span> {`=> 新：`}</span>
        <span style={{ fontWeight: "bold" }}>{`${newText}`}</span>
      </span>
    );
  };
  return (
    // box with border
    <Box sx={{ border: 1, borderColor: "grey.500", p: 2 }}>
      <Typography>
        {/*{`版本: ${history.version} 修改日期: ${history.validFromDate}`}*/}
        <span style={{ fontWeight: "bold", color: "red" }}>
          {`版本: ${history.version}`}
        </span>
        <span style={{ fontWeight: "bold", color: "blue" }}>
          {` 修改日期: ${history.validFromDate}`}
        </span>
      </Typography>
      <Typography>
        <span style={{ fontWeight: "bold", color: "grey" }}>
          {`修改人: ${history.createdByName}`}
        </span>
      </Typography>
      {history.diff.attributes && (
        <Typography>
          {Object.entries(history.diff.attributes).map(([key, value]) => {
            const field = fields.find((field) => field.id === Number(key));
            return (
              <Typography key={key}>
                <span style={{ fontWeight: "bold" }}>
                  {field ? field.name : key}
                </span>
                : {oldNewText(value)}
              </Typography>
            );
          })}
        </Typography>
      )}
      {history.diff.kdPriceLimit && (
        <Typography>
          {history.diff.kdPriceLimit.map((limit) => (
            <Typography key={limit.code}>
              {limit.code}: {oldNewText(limit)}
            </Typography>
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default MaterialAttributesPage;
